// Section 24.15 debug tab (development builds only): raw state JSON viewer, rng state, pending
// prompts, "run the steward for N seasons" and "fast forward to the first shortage". A tab root.
import { useState } from 'preact/hooks';
import {
  diagnosticsOf,
  fastForwardToShortage,
  game,
  runStewardFor,
} from '../../store';
import { debugVM, stateJson } from '../../viewModels';
import { uiText } from '../text/en';
import { copyText } from './notices';

export function DebugTab() {
  const g = game.value;
  const [seasons, setSeasons] = useState(4);
  const [key, setKey] = useState<string | null>('people');
  const [result, setResult] = useState<string | null>(null);
  if (!import.meta.env.DEV || g === null) return null;
  const vm = debugVM(g);
  const t = uiText.debug;
  const prompts = (list: typeof vm.pendingPrompts) =>
    list.length === 0 ? t.none : list.map((p) => `${p.kind} ${p.id}`).join(', ');
  return (
    <section class="debug" aria-labelledby="debug-title">
      <h2 id="debug-title">{t.title}</h2>
      <dl class="debug__facts">
        <dt>{t.rng}</dt>
        <dd class="num">
          {t.main} {vm.rngState} · {t.events} {vm.eventRngState}
        </dd>
        <dt>{t.cursor}</dt>
        <dd class="num">{vm.phaseCursor ?? t.none}</dd>
        <dt>{t.pending}</dt>
        <dd>{prompts(vm.pendingPrompts)}</dd>
        <dt>{t.open}</dt>
        <dd>{prompts(vm.openPrompts)}</dd>
      </dl>
      <form
        class="debug__row"
        onSubmit={(e) => {
          e.preventDefault();
          setResult(String(runStewardFor(seasons)));
        }}
      >
        <label>
          {t.runSteward}{' '}
          <input
            type="number"
            min={1}
            max={400}
            value={seasons}
            onInput={(e) => setSeasons(Math.max(1, Number(e.currentTarget.value) || 1))}
          />{' '}
          {t.seasons}
        </label>{' '}
        <button type="submit" class="button button--small">
          {t.run}
        </button>{' '}
        <button
          type="button"
          class="button button--small"
          onClick={() => setResult(t.fastForwardResult(fastForwardToShortage()))}
        >
          {t.fastForward}
        </button>{' '}
        <button
          type="button"
          class="button button--small"
          onClick={() => void copyText(JSON.stringify(diagnosticsOf(g, 'debug', '')))}
        >
          {t.copyDiagnostics}
        </button>
        {result !== null && <span class="hint" role="status"> {result}</span>}
      </form>
      <label class="field">
        <span class="field__label">{t.key}</span>
        <select value={key ?? ''} onChange={(e) => setKey(e.currentTarget.value === '' ? null : e.currentTarget.value)}>
          <option value="">{t.whole}</option>
          {vm.keys.map((k) => (
            <option key={k.key} value={k.key}>
              {k.key} ({k.size})
            </option>
          ))}
        </select>
      </label>
      <pre class="debug__json" tabIndex={0} aria-label={t.state}>
        {stateJson(g, key)}
      </pre>
    </section>
  );
}
